/**
 * Sending one packaged recording: ask for a place to put it, upload it, hand it
 * to the processor. The same three steps, in the same order, as the Record
 * page's archive upload, with the same two idempotency rules:
 *
 *   - a grant that has expired is asked for again under a FRESH pair of keys
 *     (the old upload key is spent on the old grant);
 *   - the job key is fingerprinted over the body, so what is submitted is
 *     exactly what `beforeSubmit` is told (the capture page writes its resume
 *     pointer from that, BEFORE the submission).
 *
 * Pulled out of `RecordCapture.tsx` for Sprint 1.9.13: a paired session sends
 * its eyes-open block from the controller while the eyes-closed block is still
 * recording (no screen shows it), and its eyes-closed block from the screen as
 * today. One function, two callers; the pointer is the caller's business.
 */

import { createUpload, submitRecordingJob, type Condition, type DeviceId, type ExperimentLink, type Job, type PairLink } from '../api'
import { newOperationKeys, type OperationKeys } from '../idempotency'
import { grantHasExpired, uploadToGrant, type UploadProgress } from '../upload'

export interface SendInput {
  userId: string
  file: File
  /** EC or EO, explicit per recording (a block names its own condition). */
  condition: Condition
  /** Omitted from the job when undefined, so the server detects the device. */
  deviceId?: DeviceId
  /** The pair link of a paired session's block; absent on a plain recording. */
  pair?: PairLink
  /** The run and role are submitted together; purpose remains server-owned. */
  experiment?: ExperimentLink
  /** The keys to use (a retry reuses the previous attempt's); fresh ones when absent. */
  keys?: OperationKeys
  /** Called once the grant is in hand, as the upload begins. */
  onUploadStart?: () => void
  onProgress?: (progress: UploadProgress) => void
  /** Called after the upload and BEFORE the submission, with exactly what the submission will send. */
  beforeSubmit?: (submission: { uploadId: string; jobKey: string }) => void
}

export interface Sent {
  job: Job
  uploadId: string
  /** The keys the successful run used: a grant re-mint replaces them, so the caller keeps these. */
  keys: OperationKeys
}

export async function sendRecording(input: SendInput): Promise<Sent> {
  let keys = input.keys ?? newOperationKeys()
  let grant = await createUpload({ userId: input.userId, idempotencyKey: keys.upload })
  if (grantHasExpired(grant)) {
    keys = newOperationKeys()
    grant = await createUpload({ userId: input.userId, idempotencyKey: keys.upload })
  }
  input.onUploadStart?.()
  await uploadToGrant(grant, input.file, { onProgress: input.onProgress })
  input.beforeSubmit?.({ uploadId: grant.upload_id, jobKey: keys.job })
  const job = await submitRecordingJob({
    userId: input.userId,
    uploadId: grant.upload_id,
    condition: input.condition,
    ...(input.deviceId !== undefined ? { deviceId: input.deviceId } : {}),
    ...(input.pair !== undefined ? { pair: input.pair } : {}),
    ...(input.experiment !== undefined ? { experiment: input.experiment } : {}),
    idempotencyKey: keys.job,
  })
  return { job, uploadId: grant.upload_id, keys }
}
